import { useCallback, useEffect, useMemo, useState } from 'react';
import { billingService, type EntitlementKey } from '../services/billingService';

/**
 * What the company's plan lets it reach. This drives what the sidebar offers,
 * but it is not the enforcement: every Advanced-only table is gated by an RLS
 * policy calling `company_has_entitlement`, so a hidden nav item and a blocked
 * query are two views of the same fact rather than two things to keep in step.
 *
 * While loading, `has` answers true. Flashing an upgrade card at a customer who
 * does have the feature is worse than a moment of over-offering, and the
 * database refuses the read either way.
 */
export function useEntitlements() {
  const [keys, setKeys] = useState<EntitlementKey[] | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setKeys(await billingService.fetchEntitlements());
      setError('');
    } catch (err) {
      // An unreadable entitlement list must not lock anyone out of the pages
      // they are entitled to; RLS still has the last word.
      console.error('Unable to load plan entitlements', err);
      setError(err instanceof Error ? err.message : 'Unable to load plan entitlements');
      setKeys(null);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const has = useCallback(
    (key: EntitlementKey) => (keys === null ? true : keys.includes(key)),
    [keys],
  );

  return useMemo(
    () => ({ entitlements: keys, isLoading: keys === null && !error, error, has, reload: load }),
    [keys, error, has, load],
  );
}
